import React from 'react';
import { AdRenderer } from '@moment/components/AdRenderer/AdRenderer.tsx';
import { GamAd } from '@moment/components/GamAd/GamAd.tsx';
import {
  DISPLAY_AD_DESKTOP_AD_MAP_DATA,
  DISPLAY_AD_MOBILE_AD_MAP_DATA,
  DISPLAY_AD_TABLED_AD_MAP_DATA,
  DISPLAY_AD_TABLED_SMALL_AD_MAP_DATA,
} from '@moment/components/DisplayAd/constants.ts';
import { AD_SIZE } from '@moment/components/GalleryAd/ad-constants.ts';

export interface AdRenderOptions {
  adUnitId: string;
  placement: 'top' | 'bot';
  containerClassName?: string;
  bannerClassName?: string;
  adBlockClassName?: string;
  key?: string;
  iabCategories?: string[];
}

/**
 * Shared utility for rendering ads with GAM/DTX logic.
 * This centralizes the ad rendering logic used by both FeedBuilder and widgets.
 */
export const createAdComponent = (
  options: AdRenderOptions
): React.ReactElement => {
  const {
    adUnitId,
    placement,
    containerClassName = 'ad--block',
    bannerClassName = 'ad--title',
    adBlockClassName = 'feed__ad',
    key = adUnitId,
    iabCategories,
  } = options;

  // Standard size configuration used across the app
  const size = [
    ...DISPLAY_AD_DESKTOP_AD_MAP_DATA.adSizeList,
    ...DISPLAY_AD_TABLED_AD_MAP_DATA.adSizeList,
    ...DISPLAY_AD_TABLED_SMALL_AD_MAP_DATA.adSizeList,
    ...DISPLAY_AD_MOBILE_AD_MAP_DATA.adSizeList,
  ];

  const extraTargeting: Record<string, string | string[]> = {};
  if (iabCategories?.length) {
    extraTargeting['iab_category_codes'] = iabCategories;
  }

  const responsiveSize = [
    DISPLAY_AD_DESKTOP_AD_MAP_DATA,
    DISPLAY_AD_TABLED_AD_MAP_DATA,
    DISPLAY_AD_TABLED_SMALL_AD_MAP_DATA,
    DISPLAY_AD_MOBILE_AD_MAP_DATA,
  ];

  // Create GAM ad component
  const gamAd = (
    <GamAd
      key={`${adUnitId}-gam`}
      adUnitId={adUnitId}
      adType={placement}
      size={size}
      responsiveSize={responsiveSize}
      containerClassName={containerClassName}
      adBlockClassName={adBlockClassName}
      extraTargeting={extraTargeting}
    />
  );

  // Use AdRenderer to handle GAM/DTX logic
  return (
    <AdRenderer
      key={key}
      placement={placement}
      adUnitId={adUnitId}
      GamComponent={gamAd}
      dtxAdSize={[AD_SIZE.small]}
      containerClassName={`dtx--${containerClassName}`}
      bannerClassName={`${bannerClassName}`}
      adBlockClassName={`dtx--${adBlockClassName}`}
      iabCategories={iabCategories}
      dtxFallbackToMrecIfBanner={false}
    />
  );
};

/**
 * Helper function to get the standard ad size arrays.
 * Useful when you need just the size configuration without rendering.
 */
export const getAdSizeConfiguration = () => ({
  size: [
    ...DISPLAY_AD_DESKTOP_AD_MAP_DATA.adSizeList,
    ...DISPLAY_AD_TABLED_AD_MAP_DATA.adSizeList,
    ...DISPLAY_AD_TABLED_SMALL_AD_MAP_DATA.adSizeList,
    ...DISPLAY_AD_MOBILE_AD_MAP_DATA.adSizeList,
  ],
  responsiveSize: [
    DISPLAY_AD_DESKTOP_AD_MAP_DATA,
    DISPLAY_AD_TABLED_AD_MAP_DATA,
    DISPLAY_AD_TABLED_SMALL_AD_MAP_DATA,
    DISPLAY_AD_MOBILE_AD_MAP_DATA,
  ],
});

// expected values for ad heights and ad banner heights if no ad height is found
export const DEFAULT_AD_HEIGHT = 250;
export const AD_BANNER_HEIGHT = 30;
export const DEFAULT_BANNER_HEIGHT = 50;
